import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Skeleton,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Button,
    useTheme,
    useMediaQuery,
    ToggleButton,
    ToggleButtonGroup,
    Drawer,
    Divider
} from '@mui/material';
import {
    TrendingUp as IncomeIcon,
    TrendingDown as ExpenseIcon,
    AccountBalance as BalanceIcon,
    Receipt as ReceivableIcon,
    Add as AddIcon,
    ArrowForward as ArrowIcon,
    Analytics as AnalyticsIcon,
    Home as SummaryIcon,
    CalendarViewMonth as MonthlyIcon,
    CalendarViewWeek as DailyIcon,
    CalendarToday as YearlyIcon,
    Visibility as ViewIcon,
    InfoOutlined as InfoIcon,
    DateRange as CustomIcon,
    CheckCircle as CheckIcon,
    Lightbulb as InsightsIcon,
    EmojiEvents as TrophyIcon,
    TrendingUp as TrendUpIcon,
    WarningAmber as WarningIcon,
    AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import {
    Tooltip,
    TextField,
    Popover,
    Stack
} from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { dashboardAPI, membersAPI } from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, getCategoryStyle } from '../config';

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

const KPICard = ({ title, value, icon, color, loading, type = 'primary' }) => {
    return (
        <Card className={`kpi-card ${type}`} sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography className="kpi-label">
                            {title}
                        </Typography>
                        {loading ? (
                            <Skeleton width={120} height={40} />
                        ) : (
                            <Typography className="kpi-value">
                                {value}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            bgcolor: `${color}15`,
                            borderRadius: '12px',
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [trendPeriod, setTrendPeriod] = useState('monthly');
    const [chartView, setChartView] = useState('all'); // all, income, expense
    // Custom Date Range State
    const [customRange, setCustomRange] = useState({
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [anchorEl, setAnchorEl] = useState(null);

    const [stats, setStats] = useState(null);
    const [memberDistribution, setMemberDistribution] = useState([]);
    const [memberChartView, setMemberChartView] = useState('net'); // net, income, expense
    const [ledgerOpen, setLedgerOpen] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [insightsOpen, setInsightsOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { user } = useAuth();

    // Color Palette
    const colors = {
        primary: '#6366f1',
        success: '#10b981',
        error: '#ef4444',
        warning: '#f59e0b',
        info: '#3b82f6',
        slate: '#64748b',
        background: '#f8fafc'
    };

    useEffect(() => {
        if (trendPeriod !== 'custom') {
            fetchDashboardStats();
        }
    }, [trendPeriod]);

    useEffect(() => {
        fetchMemberDistribution();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            const params = { period: trendPeriod };
            if (trendPeriod === 'custom') {
                params.startDate = customRange.startDate;
                params.endDate = customRange.endDate;
            }
            const response = await dashboardAPI.getStats(params);
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const getPeriodInfo = () => {
        switch (trendPeriod) {
            case 'daily': return 'Last 60 days, grouped by day';
            case 'monthly': return 'Last 24 months, grouped by month';
            case 'yearly': return 'Last 5 years, grouped by year';
            case 'custom': return `From ${formatDate(customRange.startDate)} to ${formatDate(customRange.endDate)}`;
            default: return '';
        }
    };

    const fetchMemberDistribution = async () => {
        try {
            const response = await dashboardAPI.getMemberDistribution();
            if (response.data.success) {
                setMemberDistribution(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching member distribution:', error);
        }
    };

    const kpiData = [
        {
            title: 'Inflow',
            value: formatCurrency(stats?.kpis?.totalIncome || 0),
            icon: <IncomeIcon />,
            color: theme.palette.success.main,
            type: 'success'
        },
        {
            title: 'Outflow',
            value: formatCurrency(stats?.kpis?.totalExpenses || 0),
            icon: <ExpenseIcon />,
            color: theme.palette.error.main,
            type: 'error'
        },
        {
            title: 'Net Fund',
            value: formatCurrency(stats?.kpis?.netBalance || 0),
            icon: <BalanceIcon />,
            color: theme.palette.primary.main,
            type: 'primary'
        },
        {
            title: 'Receivables',
            value: formatCurrency(stats?.kpis?.totalReceivables || 0),
            icon: <ReceivableIcon />,
            color: theme.palette.warning.main,
            type: 'warning'
        }
    ];

    // Monthly trend chart data
    const trendChartData = {
        labels: stats?.trend?.map(item => item.label) || [],
        datasets: [
            {
                label: 'Income',
                data: stats?.trend?.map(item => item.income) || [],
                backgroundColor: colors.success,
                borderRadius: 4,
                barThickness: isMobile ? 8 : 20,
                hidden: chartView === 'expense'
            },
            {
                label: 'Expenses',
                data: stats?.trend?.map(item => item.expenses) || [],
                backgroundColor: colors.error,
                borderRadius: 4,
                barThickness: isMobile ? 8 : 20,
                hidden: chartView === 'income'
            }
        ]
    };

    const memberChartData = {
        labels: memberDistribution.map(m => m.name),
        datasets: [
            {
                data: memberDistribution.map(m => {
                    if (memberChartView === 'income') return parseFloat(m.total_income) || 0;
                    if (memberChartView === 'expense') return parseFloat(m.total_expenses) || 0;
                    return parseFloat(m.net_investment) || 0;
                }),
                backgroundColor: [
                    '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'
                ],
                borderWidth: 0,
                hoverOffset: 10
            }
        ]
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Premium Animated Helix Logo */}
                    <Box sx={{
                        position: 'relative',
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {/* Rotating Helix Ring 1 */}
                        <Box sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            border: '2px solid transparent',
                            borderTopColor: 'primary.main',
                            borderBottomColor: 'secondary.main',
                            borderRadius: '50%',
                            animation: 'spin 3s linear infinite'
                        }} />
                        {/* Rotating Helix Ring 2 */}
                        <Box sx={{
                            position: 'absolute',
                            width: '85%',
                            height: '85%',
                            border: '2px solid transparent',
                            borderLeftColor: 'primary.light',
                            borderRightColor: 'secondary.light',
                            borderRadius: '50%',
                            animation: 'spinReverse 4s linear infinite'
                        }} />
                        {/* Logo Icon */}
                        <Box
                            component="img"
                            src="/buds-logo.png"
                            alt="Buds Logo"
                            sx={{
                                height: 40,
                                width: 40,
                                objectFit: 'contain',
                                zIndex: 1
                            }}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=B'; }}
                        />
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h5" fontWeight={800} sx={{ color: 'text.primary' }}>
                                {isMobile && showAnalytics ? 'Analytics' : 'Dashboard'}
                            </Typography>
                            <IconButton
                                onClick={() => setInsightsOpen(true)}
                                size="small"
                                sx={{
                                    bgcolor: '#fef3c7',
                                    color: '#d97706',
                                    '&:hover': { bgcolor: '#fde68a' },
                                    width: 32,
                                    height: 32,
                                    boxShadow: '0 2px 4px rgba(217,119,6,0.1)'
                                }}
                            >
                                <InsightsIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, textTransform: 'uppercase', fontWeight: 600 }}>
                            <img src="/buds-full-logo.png" alt="Budscube" style={{ height: '12px' }} /> | Spicy Aliyadans
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {user && user.role !== 'VIEWER' && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/transactions/new')}
                            size={isMobile ? "small" : "medium"}
                        >
                            {isMobile ? 'Add' : 'Add Transaction'}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Content Logic: Mobile showAnalytics vs Summary vs Desktop */}

            {(!isMobile || (isMobile && !showAnalytics)) && (
                <>
                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {kpiData.map((kpi, index) => (
                            <Grid item xs={6} md={3} key={index}>
                                <KPICard {...kpi} loading={loading} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Mobile Analytics Toggle Button */}
                    {isMobile && (
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AnalyticsIcon />}
                            onClick={() => setShowAnalytics(true)}
                            sx={{
                                mb: 3,
                                py: 1.5,
                                borderRadius: 2,
                                borderWidth: 2,
                                '&:hover': { borderWidth: 2 },
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem'
                            }}
                        >
                            View Detailed Analysis & Trends
                        </Button>
                    )}
                </>
            )}

            {/* Charts Row - Show on Desktop OR Mobile when showAnalytics is TRUE */}
            {(!isMobile || (isMobile && showAnalytics)) && (
                <>
                    {isMobile && (
                        <Button
                            fullWidth
                            variant="text"
                            startIcon={<SummaryIcon />}
                            onClick={() => setShowAnalytics(false)}
                            sx={{ mb: 2, fontWeight: 600 }}
                        >
                            Back to Dashboard Summary
                        </Button>
                    )}
                    <Grid container spacing={3} sx={{ mb: 3 }} direction={isMobile ? "column-reverse" : "row"}>
                        {/* Member Distribution */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            Partners
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={memberChartView}
                                            exclusive
                                            onChange={(e, val) => val && setMemberChartView(val)}
                                            size="small"
                                            sx={{
                                                bgcolor: '#f1f5f9',
                                                borderRadius: '8px',
                                                height: 28,
                                                '& .MuiToggleButton-root': {
                                                    border: 'none',
                                                    borderRadius: '8px !important',
                                                    px: 1,
                                                    fontSize: '0.65rem',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    color: '#64748b'
                                                },
                                                '& .Mui-selected': {
                                                    bgcolor: '#fff !important',
                                                    color: 'primary.main !important',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <ToggleButton value="net">Net</ToggleButton>
                                            <ToggleButton value="income">Income</ToggleButton>
                                            <ToggleButton value="expense">Expense</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                        {memberChartView === 'net' ? 'Net Investment Balance' : memberChartView === 'income' ? 'Total Income/Equity Share' : 'Total Expenditure Share'}
                                    </Typography>

                                    {loading ? (
                                        <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', mt: 2 }} />
                                    ) : (
                                        <Box sx={{ flexGrow: 1, minHeight: isMobile ? 300 : 400, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                            <Doughnut
                                                data={memberChartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    cutout: '70%',
                                                    plugins: {
                                                        legend: {
                                                            position: 'bottom',
                                                            labels: {
                                                                usePointStyle: true,
                                                                boxWidth: 8,
                                                                padding: 10,
                                                                font: { family: 'Inter', size: 10, weight: 600 },
                                                                color: '#64748b'
                                                            }
                                                        },
                                                        tooltip: {
                                                            backgroundColor: '#1e293b',
                                                            padding: 12,
                                                            titleFont: { family: 'Inter', size: 13 },
                                                            bodyFont: { family: 'Inter', size: 12 },
                                                            cornerRadius: 8,
                                                            callbacks: {
                                                                label: (context) => {
                                                                    const member = memberDistribution[context.dataIndex];
                                                                    return [
                                                                        ` ${context.label}`,
                                                                        ` Income: ${formatCurrency(member.total_income)}`,
                                                                        ` Expense: ${formatCurrency(member.total_expenses)}`,
                                                                        ` Net: ${formatCurrency(member.net_investment)}`
                                                                    ];
                                                                }
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                            <Box sx={{ position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0, fontWeight: 600 }}>Total {memberChartView.charAt(0).toUpperCase() + memberChartView.slice(1)}</Typography>
                                                <Typography variant="h6" fontWeight={800} color="primary.main">
                                                    {formatCurrency(memberChartData.datasets[0].data.reduce((a, b) => (parseFloat(a) || 0) + (parseFloat(b) || 0), 0))}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    <Button
                                        fullWidth
                                        variant="text"
                                        color="primary"
                                        onClick={() => setLedgerOpen(!ledgerOpen)}
                                        sx={{ mt: 'auto', pt: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }}
                                    >
                                        {ledgerOpen ? 'Hide More Information' : 'View More Information'}
                                    </Button>

                                    {ledgerOpen && !loading && (
                                        <Box sx={{ mt: 2, borderTop: '1px dashed #e2e8f0', pt: 2 }}>
                                            <List disablePadding>
                                                {memberDistribution.map(m => (
                                                    <ListItem key={m.id} sx={{ px: 0, py: 0.5 }}>
                                                        <ListItemText
                                                            primary={m.name}
                                                            primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 700 }}
                                                            secondary={`${m.pepper_plants} Plants • ${m.pvc_pipes} Pipes`}
                                                        />
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                fontWeight={800}
                                                                sx={{
                                                                    color: m.net_investment < 0 ? colors.error : colors.success
                                                                }}
                                                            >
                                                                {formatCurrency(m.net_investment)}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                                <span style={{ color: colors.success }}>+{formatCurrency(m.total_income)}</span> / <span style={{ color: colors.error }}>-{formatCurrency(m.total_expenses)}</span>
                                                            </Typography>
                                                        </Box>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Monthly Trend */}
                        <Grid item xs={12} md={8}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pb: '16px !important' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {trendPeriod.charAt(0).toUpperCase() + trendPeriod.slice(1)} Trend
                                            <Tooltip title={getPeriodInfo()}>
                                                <IconButton size="small" sx={{ ml: 0.5, opacity: 0.6 }}>
                                                    <InfoIcon fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                            {/* Period Toggle */}
                                            <ToggleButtonGroup
                                                value={trendPeriod}
                                                exclusive
                                                onChange={(e, val) => {
                                                    if (val === 'custom') {
                                                        setAnchorEl(e.currentTarget);
                                                    } else if (val) {
                                                        setTrendPeriod(val);
                                                    }
                                                }}
                                                size="small"
                                                disabled={loading}
                                                sx={{
                                                    bgcolor: '#f1f5f9',
                                                    borderRadius: '8px',
                                                    height: 32,
                                                    '& .MuiToggleButton-root': {
                                                        border: 'none',
                                                        borderRadius: '8px !important',
                                                        px: 1.5,
                                                        fontSize: '0.7rem',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        color: '#64748b'
                                                    },
                                                    '& .Mui-selected': {
                                                        bgcolor: '#fff !important',
                                                        color: '#6366f1 !important',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                <ToggleButton value="daily">Daily</ToggleButton>
                                                <ToggleButton value="monthly">Monthly</ToggleButton>
                                                <ToggleButton value="yearly">Yearly</ToggleButton>
                                                <ToggleButton value="custom" sx={{ display: 'flex', gap: 0.5 }}>
                                                    <CustomIcon sx={{ fontSize: '0.9rem' }} />
                                                    Custom
                                                </ToggleButton>
                                            </ToggleButtonGroup>

                                            {/* Custom Range Popover */}
                                            <Popover
                                                open={Boolean(anchorEl)}
                                                anchorEl={anchorEl}
                                                onClose={() => setAnchorEl(null)}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                PaperProps={{ sx: { p: 2, mt: 1, borderRadius: 3, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)' } }}
                                            >
                                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>Select Date Range</Typography>
                                                <Stack spacing={2} sx={{ minWidth: 200 }}>
                                                    <TextField
                                                        label="Start Date"
                                                        type="date"
                                                        size="small"
                                                        value={customRange.startDate}
                                                        onChange={(e) => setCustomRange(prev => ({ ...prev, startDate: e.target.value }))}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <TextField
                                                        label="End Date"
                                                        type="date"
                                                        size="small"
                                                        value={customRange.endDate}
                                                        onChange={(e) => setCustomRange(prev => ({ ...prev, endDate: e.target.value }))}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        fullWidth
                                                        startIcon={<CheckIcon />}
                                                        onClick={() => {
                                                            setTrendPeriod('custom');
                                                            setAnchorEl(null);
                                                            fetchDashboardStats();
                                                        }}
                                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                                    >
                                                        Apply Range
                                                    </Button>
                                                </Stack>
                                            </Popover>

                                            {/* View Toggle */}
                                            <ToggleButtonGroup
                                                value={chartView}
                                                exclusive
                                                onChange={(e, val) => val && setChartView(val)}
                                                size="small"
                                                disabled={loading}
                                                sx={{
                                                    bgcolor: '#f1f5f9',
                                                    borderRadius: '8px',
                                                    height: 32,
                                                    '& .MuiToggleButton-root': {
                                                        border: 'none',
                                                        borderRadius: '8px !important',
                                                        px: 1.5,
                                                        fontSize: '0.7rem',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        color: '#64748b'
                                                    },
                                                    '& .Mui-selected': {
                                                        bgcolor: '#fff !important',
                                                        color: '#0f172a !important',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                <ToggleButton value="all">All</ToggleButton>
                                                <ToggleButton value="income">Income</ToggleButton>
                                                <ToggleButton value="expense">Expense</ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>
                                    </Box>

                                    <Box sx={{ flexGrow: 1, minHeight: 400, position: 'relative' }}>
                                        {loading ? (
                                            <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
                                        ) : stats?.trend?.length > 0 ? (
                                            <Bar
                                                data={trendChartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            position: 'bottom',
                                                            labels: {
                                                                usePointStyle: true,
                                                                boxWidth: 8,
                                                                padding: 15,
                                                                font: { family: 'Inter', size: 11, weight: 600 }
                                                            }
                                                        },
                                                        tooltip: {
                                                            backgroundColor: '#1e293b',
                                                            padding: 12,
                                                            titleFont: { family: 'Inter', size: 13 },
                                                            bodyFont: { family: 'Inter', size: 12 },
                                                            cornerRadius: 8,
                                                            displayColors: false,
                                                            callbacks: {
                                                                label: (context) => ` ${context.dataset.label}: ${formatCurrency(context.raw)}`
                                                            }
                                                        }
                                                    },
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            grid: { color: '#f1f5f9', strokeDashArray: 4 },
                                                            ticks: {
                                                                font: { family: 'Inter', size: 10 },
                                                                color: '#94a3b8',
                                                                callback: (value) => '₹' + value.toLocaleString('en-IN')
                                                            }
                                                        },
                                                        x: {
                                                            grid: { display: false },
                                                            ticks: {
                                                                font: { family: 'Inter', size: 10 },
                                                                color: '#94a3b8'
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', borderRadius: 2 }}>
                                                <Typography color="text.secondary">No data for this period</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Recent Activity Row - Show on Desktop OR Mobile when showAnalytics is FALSE */}
            {(!isMobile || (isMobile && !showAnalytics)) && (
                <Grid container spacing={3}>
                    {/* Recent Transactions */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">Recent Transactions</Typography>
                                    <Button size="small" endIcon={<ArrowIcon />} onClick={() => navigate('/transactions')}>
                                        View All
                                    </Button>
                                </Box>
                                {loading ? (
                                    [...Array(3)].map((_, i) => (
                                        <Skeleton key={i} height={60} sx={{ mb: 1 }} />
                                    ))
                                ) : (
                                    <List disablePadding>
                                        {stats?.recentTransactions?.slice(0, 5).map((txn) => (
                                            <ListItem key={txn.id} divider sx={{ px: 0 }}>
                                                <ListItemText
                                                    primary={txn.particulars}
                                                    secondary={formatDate(txn.transaction_date)}
                                                    primaryTypographyProps={{ noWrap: true }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <Box sx={{ textAlign: 'right' }}>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                            color={txn.category_type === 'EXPENSE' ? 'error' : 'success.main'}
                                                        >
                                                            {txn.category_type === 'EXPENSE' ? '-' : '+'}{formatCurrency(txn.amount)}
                                                        </Typography>
                                                        <Chip
                                                            label={txn.category_name}
                                                            size="small"
                                                            sx={{
                                                                fontSize: 10,
                                                                height: 18,
                                                                bgcolor: getCategoryStyle(txn.category_name).bg,
                                                                color: getCategoryStyle(txn.category_name).text,
                                                                border: `1px solid ${getCategoryStyle(txn.category_name).border}`,
                                                                fontWeight: 600,
                                                                mt: 0.5
                                                            }}
                                                        />
                                                    </Box>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                        {(!stats?.recentTransactions || stats.recentTransactions.length === 0) && (
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                                No transactions yet
                                            </Typography>
                                        )}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Pending Invoices */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">Pending Invoices</Typography>
                                    <Button size="small" endIcon={<ArrowIcon />} onClick={() => navigate('/invoices')}>
                                        View All
                                    </Button>
                                </Box>
                                {loading ? (
                                    [...Array(3)].map((_, i) => (
                                        <Skeleton key={i} height={60} sx={{ mb: 1 }} />
                                    ))
                                ) : (
                                    <List disablePadding>
                                        {stats?.pendingInvoices?.slice(0, 5).map((inv) => (
                                            <ListItem key={inv.id} divider sx={{ px: 0 }}>
                                                <ListItemText
                                                    primary={inv.customer_name}
                                                    secondary={`${inv.invoice_number} • Due: ${formatDate(inv.due_date)}`}
                                                />
                                                <ListItemSecondaryAction>
                                                    <Box sx={{ textAlign: 'right' }}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {formatCurrency(inv.balance_due)}
                                                        </Typography>
                                                        <Chip
                                                            label={inv.payment_status}
                                                            size="small"
                                                            color={inv.payment_status === 'PARTIAL' ? 'warning' : 'default'}
                                                            sx={{ fontSize: 10, height: 20 }}
                                                        />
                                                    </Box>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                        {(!stats?.pendingInvoices || stats.pendingInvoices.length === 0) && (
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                                No pending invoices
                                            </Typography>
                                        )}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )
            }
            {/* Insights Drawer */}
            <Drawer
                anchor="right"
                open={insightsOpen}
                onClose={() => setInsightsOpen(false)}
                PaperProps={{
                    sx: { width: isMobile ? '100%' : 360, p: 3, borderLeft: 'none', boxShadow: '-10px 0 30px rgba(0,0,0,0.05)' }
                }}
            >
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1, bgcolor: '#fef3c7', borderRadius: '12px' }}>
                            <InsightsIcon sx={{ color: '#d97706' }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={800}>Buds Insights</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Spicy Aliyadans Analysis</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setInsightsOpen(false)} size="small">
                        <ArrowIcon sx={{ transform: 'rotate(-90deg)' }} />
                    </IconButton>
                </Box>

                <Stack spacing={3}>
                    {/* Financial Summary Highlight */}
                    <Box sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: 3, border: '1px solid #e0f2fe' }}>
                        <Typography variant="subtitle2" fontWeight={800} color="#0369a1" gutterBottom
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendUpIcon fontSize="small" /> Financial Health
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            The farm is currently operating at <strong>{Math.round((stats?.kpis?.totalIncome / stats?.kpis?.totalExpenses) * 100) || 0}%</strong> equity coverage.
                            The net fund stands at <strong style={{ color: colors.primary }}>{formatCurrency(stats?.kpis?.netBalance)}</strong>.
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Partner Highlights */}
                    <Typography variant="subtitle1" fontWeight={800}>Partner Standings</Typography>

                    {memberDistribution.length > 0 && (
                        <>
                            {/* Top Investor */}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Box sx={{ p: 1, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                                    <TrophyIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>Top Net Investor</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {[...memberDistribution].sort((a, b) => b.net_investment - a.net_investment)[0]?.name} is leading with a net investment of <strong>{formatCurrency([...memberDistribution].sort((a, b) => b.net_investment - a.net_investment)[0]?.net_investment)}</strong>.
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Highest Contributor to Expenses */}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 2 }}>
                                    <WalletIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>Primary Resource Holder</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {[...memberDistribution].sort((a, b) => b.total_expenses - a.total_expenses)[0]?.name} holds the largest resource share (Expenses) at <strong>{formatCurrency([...memberDistribution].sort((a, b) => b.total_expenses - a.total_expenses)[0]?.total_expenses)}</strong>.
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}

                    <Divider />

                    {/* Actionable Advice */}
                    <Box sx={{ p: 2, bgcolor: '#fff7ed', borderRadius: 3, border: '1px solid #ffedd5' }}>
                        <Typography variant="subtitle2" fontWeight={800} color="#9a3412" gutterBottom
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon fontSize="small" /> Attention Needed
                        </Typography>
                        {memberDistribution.some(m => m.net_investment < 0) ? (
                            <Typography variant="body2" color="text.secondary">
                                One or more partners have a <strong>negative balance</strong>. Consider a fund top-up to balance the ledger.
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                All partner accounts are currently in the green. The ledger is well-balanced.
                            </Typography>
                        )}
                    </Box>

                    {/* Category Fact */}
                    <Box sx={{ mt: 'auto', pt: 4, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            "Data-driven decisions lead to sustainable growth."
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                            <img src="/buds-full-logo.png" alt="Budscube" style={{ height: '14px', opacity: 0.5 }} />
                        </Box>
                    </Box>
                </Stack>
            </Drawer>
        </Box>
    );
};

export default Dashboard;
